import { Key, useCallback, useState } from "react";
import { getKeyValue, Tab, Tabs } from "@nextui-org/react";
import { useGetAllUsers } from "@/services/admin.service";
import { format } from "date-fns";
import Datatable from "@/components/tables/datatable";
import { getLocalTimeZone, parseDate } from "@internationalized/date";
import { useGetGameSessionsByUsername } from "@/services/game.service";
import { DateRangePicker } from "@/components/ui/daterangepicker";
import { getPresetDate } from "@/utils/common";

export const ReportModuleTabs = () => {
  const [selectedTab, setSelectedTab] = useState("accountlist");

  return (
    <div className="flex w-full flex-col justify-evenly">
      <Tabs
        variant="underlined"
        aria-label="User Module"
        className="w-full justify-start"
        classNames={{
          tabList: ["overflow-x-auto text-[#FFFFFF80]"],
          cursor: "user-module-selected",
          tabContent:
            "text-base font-bold group-data-[selected=true]:text-[#FFFFFF]",
        }}
        selectedKey={selectedTab}
        onSelectionChange={(key) => setSelectedTab(key as string)}
      >
        <Tab key="playerGameReport" title="Player Game Report">
          <PlayerGameReport />
        </Tab>
      </Tabs>
    </div>
  );
};

interface PlayerGameReportFilter {
  username: string;
  startDate: string;
  endDate: string;
}

const PlayerGameReport = () => {
  const [filter, setFilter] = useState<PlayerGameReportFilter>({
    username: "",
    startDate: format(new Date(), "yyyy-MM-dd"),
    endDate: format(new Date(), "yyyy-MM-dd"),
  });

  const columns = [
    { key: "no", label: "No" },
    { key: "game", label: "Game Name", sortable: true },
    { key: "provider", label: "Provider Name", sortable: true },
    { key: "wager", label: "Wager", sortable: true },
    { key: "winning", label: "Winning", sortable: true },
    { key: "winloss", label: "Win Loss", sortable: true },
    { key: "createdAt", label: "Start Date", sortable: true },
    { key: "updatedAt", label: "Settle Date", sortable: true },
    { key: "id", label: "Round ID" },
  ];

  const fields = [{ label: "Username", value: "userName" }];

  const query = useGetGameSessionsByUsername;

  const renderCell = useCallback((data: any[], item: any, columnKey: Key) => {
    switch (columnKey) {
      case "no":
        return (
          <div className="">
            {" "}
            {data.findIndex((row) => (row as any)?.id === (item as any)?.id) +
              1}
          </div>
        );
      case "wager":
      case "winning":
        return (
          <div className="">
            {getKeyValue(item, columnKey).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            }) || "0"}
          </div>
        );
      case "winloss":
        return (
          <div
            className={`${getKeyValue(item, columnKey) < 0 ? "text-danger" : "text-success"}`}
          >
            {getKeyValue(item, columnKey).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            }) || "0"}
          </div>
        );
      case "createdAt":
      case "updatedAt":
        return (
          <div className="w-max">
            <div>
              {getKeyValue(item, columnKey)
                ? format(getKeyValue(item, columnKey), "dd/MM/yyyy HH:ii:ss")
                : "-"}
            </div>
          </div>
        );
      default:
        return (
          <div className="font-normal">
            {getKeyValue(item, columnKey as string) || "-"}
          </div>
        );
    }
  }, []);

  return (
    <div>
      <div>
        <Datatable
          columns={columns}
          renderCell={renderCell}
          query={query}
          defaultSort="createdAt"
          defaultOrder="desc"
          customQueryFilter={{
            ...filter,
          }}
          customSummaryComponent={(data) => {
            return (
              <table className="my-2 border-separate border-spacing-2 border">
                <tr className="border-separate border-spacing-2 border text-start">
                  <th className="min-w-32 text-start">Total Bet</th>
                  <th className="min-w-32 text-start">Total Winning</th>
                  <th className="min-w-32 text-start">Total Winloss</th>
                </tr>
                <tr className="border-separate border-spacing-2 border text-start">
                  <td>
                    {data?.total?.wager?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td>
                    {data?.total?.winning?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td
                    className={`${data?.total?.winloss < 0 ? "text-danger" : "text-success"}`}
                  >
                    {data?.total?.winloss?.toLocaleString(undefined, {
                      maximumFractionDigits: 2,
                    })}
                  </td>
                </tr>
              </table>
            );
          }}
          customFilters={[
            <DateRangePicker
              onChange={(e: any) => {
                const startDate = format(
                  e.start.toDate(getLocalTimeZone()),
                  "yyyy-MM-dd",
                );
                const endDate = format(
                  e.end.toDate(getLocalTimeZone()),
                  "yyyy-MM-dd",
                );
                const newFilter = { ...filter, startDate, endDate };
                setFilter(newFilter);
              }}
            />,
          ]}
          searchFields={fields}
        />
      </div>
    </div>
  );
};
